import {
  licenceFormSchema,
  licenceFormValidator
} from "modules/tenants/sections/plt/licencing/licence-form-dialog/LicenceFormDialog.validator";
import React, { FunctionComponent } from "react";

import { Dialog, SubmissionForm } from "@bps/fluent-ui";
import { HttpError } from "@bps/http-client";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import {
  useAddLicence,
  useLicence,
  useUpdateLicence
} from "@libs/api/gateways/plt/plt-gateway.hooks";

import {
  LicenceFormDialogBaseProps,
  LicenceFormDialogProps,
  LicenceFormValues,
  newLicenceFormValues
} from "./LicenceFormDialog.types";
import { LicenceFormFields } from "./LicenceFormFields";
import { getLicenceFormValues } from "./utils";

const LicenceFormDialogBase: FunctionComponent<Omit<
  LicenceFormDialogBaseProps,
  "licenceId"
> & {
  tenantId: string;
  licenceId: "new" | string;
}> = ({ closeDialog, licenceId, tenantId }) => {
  const isNewLicence = licenceId === "new";

  const licenceQuery = useLicence(licenceId, {
    enabled: !isNewLicence
  });

  const { mutateAsync: addLicence } = useAddLicence();
  const { mutateAsync: updateLicence } = useUpdateLicence();

  const onSubmit = async (values: LicenceFormValues) => {
    try {
      const { expiryDate, isActive, ...rest } = values;
      if (!!expiryDate) {
        const endOfDay = new Date(expiryDate);
        endOfDay.setHours(23, 59, 59);
        const iso = endOfDay.toISOString();

        if (isNewLicence) {
          await addLicence({
            ...rest,
            tenantId,
            expiryDate: iso,
            isInactive: !isActive,
            doNotRenew: true,
            source: "MANUAL"
          });
        } else {
          licenceQuery.data &&
            (await updateLicence({
              ...licenceQuery.data,
              ...rest,
              expiryDate: iso,
              isInactive: !isActive
            }));
        }
        closeDialog();
      }
    } catch (e) {
      const error = e as HttpError;
      throw new Error((error.detail as string) ?? error.message);
    }
  };

  const title: string = `${isNewLicence ? "New" : "Edit"} Licence`;

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        title,
        showCloseButton: true,
        onDismiss: closeDialog
      }}
      minWidth={320}
      styles={{ root: { minHeight: 480 } }}
    >
      <QueryStateIndicator {...licenceQuery} allowNullOrUndefined>
        {data => {
          const defaultValues = isNewLicence
            ? newLicenceFormValues
            : getLicenceFormValues(data);
          return (
            <SubmissionForm<LicenceFormValues>
              onSubmit={onSubmit}
              initialValues={defaultValues}
              validate={values =>
                licenceFormValidator.validate(values, licenceFormSchema)
              }
              hideCancelButton
            >
              {() => (
                <LicenceFormFields
                  tenantId={tenantId}
                  isManagedLicence={
                    data?.source !== undefined && data?.source !== "MANUAL"
                  }
                />
              )}
            </SubmissionForm>
          );
        }}
      </QueryStateIndicator>
    </Dialog>
  );
};

export const LicenceFormDialog: FunctionComponent<LicenceFormDialogProps> = props => {
  if (!props.licenceId) return null;
  return (
    <LicenceFormDialogBase
      {...props}
      licenceId={props.licenceId}
      isManagedLicence={props.source !== undefined && props.source !== "MANUAL"}
    />
  );
};
